import type { Extension } from "@codemirror/state";
import { buttonStyle, ButtonVariant } from "@repo/ui/Buttons/button";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { Size } from "@repo/ui/helpers/helpers";
import { IconCirclePlusMinus } from "@tabler/icons-react";
import { useId, useState, type FC, type ReactNode, type Ref } from "react";
import { ToggleButton } from "react-aria-components";
import { LazyProseEditor } from "./lazyProseEditor.js";
import type { ProseEditorHandle } from "./proseEditor.js";

interface FieldDiffState {
  /** The field's value differs from what it is diffed against. */
  hasChanges: boolean;
  /** Only while the toggle is on and there is something to compare. */
  isShowingChanges: boolean;
  onToggle: () => void;
}

/**
 * Whether a field shows its changes. It stops when there's nothing to diff, or
 * when `originalValue` changes.
 */
export function useFieldDiffState(
  originalValue: string | undefined,
  value: string,
): FieldDiffState {
  const [isShowingChanges, setIsShowingChanges] = useState(false);
  const [comparedValue, setComparedValue] = useState(originalValue);
  const hasChanges = originalValue !== undefined && originalValue !== value;
  if (originalValue !== comparedValue) {
    setComparedValue(originalValue);
    setIsShowingChanges(false);
  } else if (!hasChanges && isShowingChanges) {
    setIsShowingChanges(false);
  }
  return {
    hasChanges,
    isShowingChanges: hasChanges && isShowingChanges,
    onToggle: () => {
      setIsShowingChanges((current) => !current);
    },
  };
}

interface FieldDiffToggleProps {
  isShowingChanges: boolean;
  onToggle: () => void;
}

export const FieldDiffToggle: FC<FieldDiffToggleProps> = (props) => {
  const { isShowingChanges, onToggle } = props;
  return (
    <ToggleButton
      isSelected={isShowingChanges}
      onChange={onToggle}
      className={buttonStyle({
        variant: ButtonVariant.Tertiary,
        size: Size.SMALL,
        className: "rounded-md font-medium hover:bg-muted selected:bg-muted",
      })}
    >
      <IconCirclePlusMinus className="size-4 shrink-0" />
      <span>{isShowingChanges ? "Hide changes" : "Show changes"}</span>
    </ToggleButton>
  );
};

export interface DiffableFieldProps {
  label: ReactNode;
  value: string;
  /** Not called when the change comes from the `value` prop. */
  onChange: (value: string) => void;
  /** Compared exactly; callers handle trimming. */
  originalValue: string | undefined;
  /** A pending Ask Alpharun suggestion, shown read-only. */
  suggestedValue?: string;
  placeholder?: string;
  maxLength?: number;
  isReadOnly?: boolean;
  /** @default "textarea" */
  variant?: "input" | "textarea";
  /** Shows the text as styled markdown. Only for `textarea`. */
  isMarkdown?: boolean;
  /**
   * Hides long unchanged stretches of the diff behind a bar, if the field is
   * read-only when the diff opens.
   */
  collapseUnchanged?: boolean;
  /** Shows a map of the changes down the left side. */
  hasDiffMinimap?: boolean;
  /** Shows the field's changes. When set, the toggle doesn't render. */
  isShowingChanges?: boolean;
  /** Help text below the editor. */
  description?: ReactNode;
  /** Classes for the editor's box, which the diff shares. */
  editorClassName?: string;
  /**
   * A handle that scrolls the editor to a line, in either view. Only for
   * `textarea`.
   */
  editorRef?: Ref<ProseEditorHandle>;
  /**
   * Added to the editor's own, in either view. Memoize them. Only for
   * `textarea`.
   */
  editorExtensions?: Extension;
}

/**
 * A labeled text field. "Show changes" diffs it against `originalValue`, or a
 * suggestion against it.
 */
export const DiffableField: FC<DiffableFieldProps> = (props) => {
  const {
    label,
    value,
    onChange,
    originalValue,
    suggestedValue,
    placeholder,
    maxLength,
    isReadOnly = false,
    variant,
    isMarkdown,
    collapseUnchanged,
    hasDiffMinimap,
    isShowingChanges,
    description,
    editorClassName,
    editorRef,
    editorExtensions,
  } = props;
  const labelId = useId();
  const descriptionId = useId();
  // A suggestion is diffed from the value; the value from the original.
  const diffFrom = suggestedValue === undefined ? originalValue : value;
  const diffTo = suggestedValue ?? value;
  const diff = useFieldDiffState(diffFrom, diffTo);
  const isDiffing =
    diff.hasChanges && (isShowingChanges ?? diff.isShowingChanges);
  // One line shows the text it was changed from, in place of a diff.
  const inputText = isDiffing && diffFrom !== undefined ? diffFrom : diffTo;

  return (
    <>
      <div className="mb-1 flex min-h-8 items-center justify-between">
        <div id={labelId}>{label}</div>
        {diff.hasChanges && isShowingChanges === undefined ? (
          <FieldDiffToggle
            isShowingChanges={diff.isShowingChanges}
            onToggle={diff.onToggle}
          />
        ) : null}
      </div>
      {variant === "input" ? (
        <LazyProseEditor
          // The text it was changed from gets its own editor, so undo in the
          // field can't bring that text back.
          key={isDiffing ? "previous" : "current"}
          value={inputText}
          onChange={onChange}
          variant="input"
          isReadOnly={isReadOnly || suggestedValue !== undefined || isDiffing}
          placeholder={placeholder}
          maxLength={maxLength}
          aria-labelledby={labelId}
          aria-describedby={description ? descriptionId : undefined}
          className={editorClassName}
        />
      ) : (
        <LazyProseEditor
          ref={editorRef}
          extensions={editorExtensions}
          value={diffTo}
          onChange={onChange}
          originalValue={isDiffing ? diffFrom : undefined}
          collapseUnchanged={collapseUnchanged}
          hasDiffMinimap={hasDiffMinimap}
          isMarkdown={isMarkdown}
          isReadOnly={isReadOnly || suggestedValue !== undefined}
          placeholder={placeholder}
          maxLength={maxLength}
          aria-labelledby={labelId}
          aria-describedby={description ? descriptionId : undefined}
          className={editorClassName}
        />
      )}
      {description ? (
        <FieldDescription id={descriptionId} className="mt-2">
          {description}
        </FieldDescription>
      ) : null}
    </>
  );
};
